import type { ReactNode } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Section } from '@/components/ui/Page'
import { formatMoneyWhole } from '@/lib/format'
import { usePackages } from '@/features/quotes/queries'
import {
  useCreateQuestionnaireTemplate,
  useDeleteQuestionnaireTemplate,
  useMakeDefaultQuestionnaire,
  useQuestionnaireTemplates,
  type QuestionnaireTemplate,
} from '@/features/questionnaires/queries'

/**
 * Settings > Templates (27 Oct 2026): the starting point for every quote,
 * invoice, contract and questionnaire, in one place. What exists already is
 * listed with a link to where it is edited; the rest is built with the
 * Documents pages.
 */
export function TemplatesSettingsPage() {
  const packages = usePackages()

  return (
    <div className="max-w-4xl pt-5">
      <Section title="Quote templates" description="Your packages. A new quote starts from one of these.">
        <ul className="flex flex-col">
          {(packages.data ?? []).map((pkg) => (
            <TemplateRow key={pkg.id} name={pkg.name} detail={formatMoneyWhole(Number(pkg.base_price))} />
          ))}
          {packages.data?.length === 0 ? <Empty>No packages yet.</Empty> : null}
        </ul>
      </Section>

      <Section title="Invoice templates" description="How a booking's total is split into invoices.">
        <ul className="flex flex-col">
          <TemplateRow
            name="Payment plans"
            detail="Deposit and instalments, with when each falls due"
            action={<Link to="/settings/payment-plans" className="underline underline-offset-4">Edit</Link>}
          />
        </ul>
      </Section>

      <Section title="Contract templates" description="The contracts you send couples, and the agreements you send your team.">
        <h3 className="label mb-1">For couples</h3>
        <ul className="mb-4 flex flex-col">
          <Empty>Coming next. Until then couples sign in Studio Ninja.</Empty>
        </ul>
        <h3 className="label mb-1">For your team</h3>
        <ul className="flex flex-col">
          <TemplateRow
            name="Crew agreement"
            detail="Sent to photographers and videographers you book"
            action={<Link to="/settings/crew-terms" className="underline underline-offset-4">Edit</Link>}
          />
        </ul>
      </Section>

      <QuestionnaireTemplates />
    </div>
  )
}

/**
 * Questionnaire templates live only here (29 Oct 2026). Sending one to a
 * couple is under Documents › Questionnaires.
 */
function QuestionnaireTemplates() {
  const templates = useQuestionnaireTemplates()
  const create = useCreateQuestionnaireTemplate()
  const makeDefault = useMakeDefaultQuestionnaire()
  const remove = useDeleteQuestionnaireTemplate()
  const navigate = useNavigate()

  const add = (from: QuestionnaireTemplate | null) =>
    create.mutate(
      from
        ? { name: `${from.name} (copy)`, intro: from.intro, questions: from.questions }
        : { name: 'New questionnaire', intro: null, questions: [] },
      { onSuccess: (id) => navigate(`/settings/templates/questionnaires/${id}`) },
    )

  return (
    <Section
      title="Questionnaire templates"
      description="What couples fill in before the wedding. New questionnaires start from the default one."
      actions={
        <Button size="sm" variant="primary" loading={create.isPending} onClick={() => add(null)}>
          New questionnaire template
        </Button>
      }
    >
      <ul className="flex flex-col">
        {(templates.data ?? [])
          .filter((template) => !template.archived)
          .map((template) => (
            <li
              key={template.id}
              className="flex flex-wrap items-center justify-between gap-3 border-b py-2.5 text-[length:var(--text-sm)] last:border-b-0"
            >
              <span className="min-w-0">
                <Link to={`/settings/templates/questionnaires/${template.id}`} className="font-semibold underline-offset-4 hover:underline">
                  {template.name}
                </Link>
                <span className="ml-2 text-taupe">
                  {template.questions.filter((question) => question.type !== 'note').length} questions
                </span>
                {template.is_default ? (
                  <Badge tone="sage" className="ml-2">
                    Default
                  </Badge>
                ) : null}
              </span>
              <span className="flex shrink-0 flex-wrap gap-1">
                {!template.is_default ? (
                  <Button size="sm" variant="ghost" loading={makeDefault.isPending} onClick={() => makeDefault.mutate(template.id)}>
                    Make default
                  </Button>
                ) : null}
                <Button size="sm" variant="ghost" onClick={() => add(template)}>
                  Duplicate
                </Button>
                {!template.is_default ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      if (window.confirm(`Delete the template "${template.name}"? Questionnaires already sent from it are kept.`))
                        remove.mutate(template.id)
                    }}
                  >
                    Delete
                  </Button>
                ) : null}
                <Link
                  to={`/settings/templates/questionnaires/${template.id}`}
                  className="inline-flex h-7 items-center rounded-md bg-[var(--color-wash-strong)] px-2.5 text-[length:var(--text-xs)] font-medium hover:bg-[var(--color-rule)]"
                >
                  Edit
                </Link>
              </span>
            </li>
          ))}
        {templates.data?.length === 0 ? <Empty>No questionnaire templates yet.</Empty> : null}
      </ul>
    </Section>
  )
}

function TemplateRow({ name, detail, action }: { name: string; detail: string; action?: ReactNode }) {
  return (
    <li className="flex items-baseline justify-between gap-4 border-b py-2.5 text-[length:var(--text-sm)] last:border-b-0">
      <span className="min-w-0">
        <span className="font-semibold">{name}</span>
        <span className="ml-2 text-taupe">{detail}</span>
      </span>
      {action ? <span className="shrink-0 text-accent">{action}</span> : null}
    </li>
  )
}

function Empty({ children }: { children: ReactNode }) {
  return <li className="py-2 text-[length:var(--text-sm)] text-taupe">{children}</li>
}
